export const performance_tutorial_config = {
    pageTitle: 'Performance Management',
    page_id: 'performance_management',
    autoStartWelcome: true,
    showFloatingButton: true,
    storagePrefix: 'performance_mgmt',
    
    // FIXED: Permission checkers - these functions should match your parent component methods
      permissionCheckers: {
        admin: function() { 
            try {
                const result = this.hasAdminAccess ? this.hasAdminAccess() : false;
                const booleanResult = Boolean(result);
                return booleanResult;
            } catch (error) {
                console.error('Error checking admin permission:', error);
                return false;
            }
        },
        
        objectives: function() { 
            try {
                const result = this.hasObjectivesAccess ? this.hasObjectivesAccess() : false;
                const booleanResult = Boolean(result);
                return booleanResult;
            } catch (error) {
                console.error('Error checking objectives permission:', error);
                return false;
            }
        },
        
        individual: function() { 
            try {
                const result = this.hasIndividualAccess ? this.hasIndividualAccess() : false;
                const booleanResult = Boolean(result);
                return booleanResult;
            } catch (error) {
                console.error('Error checking individual permission:', error);
                return false;
            }
        },
        
        dashboard: function() { 
            try {
                const result = this.has_dashboard_access ? this.has_dashboard_access() : false;
                const booleanResult = Boolean(result);
                return booleanResult;
            } catch (error) {
                console.error('Error checking dashboard permission:', error);
                return false;
            }
        },
        
        manager: function() { 
            try {
                let result = false;
                
                if (this.is_manager && typeof this.is_manager === 'function') {
                    result = this.is_manager();
                } else if (this.isManager && typeof this.isManager === 'function') {
                    result = this.isManager();
                } else if (this.has_manager_access && typeof this.has_manager_access === 'function') {
                    result = this.has_manager_access();
                } else if (this.managerAccess && typeof this.managerAccess === 'function') {
                    result = this.managerAccess();
                } else if (typeof this.is_manager !== 'undefined') {
                    result = Boolean(this.is_manager);
                } else {
                    result = Boolean(
                        this.isManager || 
                        this.managerRole || 
                        this.manager_role ||
                        this.userRole === 'manager' ||
                        this.role === 'manager'
                    );
                }
                
                const booleanResult = Boolean(result);
                return booleanResult;
                
            } catch (error) {
                console.error('Error checking manager permission:', error);
                return false;
            }
        },
        
        employee: function() { 
            try {
                const result = this.is_logged_in ? this.is_logged_in() : false;
                const booleanResult = Boolean(result);
                return booleanResult;
            } catch (error) {
                console.error('Error checking employee permission:', error);
                return false;
            }
        }
    },
    
    roleBadges: {
        admin: { text: 'Admin Access' },
        objectives: { text: 'Objectives Access' },
        individual: { text: 'Individual Access' },
        dashboard: { text: 'Dashboard Access' },
        manager: { text: 'Manager Role' },
        employee: { text: 'Employee Role' }
    },
    
    // Tutorial step definitions
    steps: [
        {
            element: [
                'select[data-bind*="performance_cycles"]',
                'select.ps-input',
                '[data-tutorial-element="cycle-selector"]'
            ],
            title: 'Performance Cycle Selection',
            description: 'Filter objective plans by performance cycle or date range.',
            permissions: [
                { type: 'individual', label: 'Individual Access' }
            ],
            requirements: 'All users',
            purpose: 'View objective plans from specific performance periods',
            position: 'bottom',
            optional: false
        },
        {
            element: [
                'button[data-bind*="btn_add_objetive_plan"]',
                'button[data-bind*="hasObjectivesOrAdmin"]',
                '.ps-btn-primary[title*="Add new objective plan"]'
            ],
            title: 'Add New Objective Plan',
            description: 'Create a new performance objective plan for an employee.',
            permissions: [
                { type: 'objectives', label: 'Objectives Access' },
                { type: 'admin', label: 'Admin Access' }
            ],
            requirements: 'Objectives Management role or Performance Admin role',
            purpose: 'Start a new performance plan for any employee.',
            position: 'left',
            optional: false
        },
        {
            element: [
                'button[data-bind*="has_dashboard_access"]',
                'a[href*="performance_dashboard"]',
                '.float-right[href*="dashboard"]'
            ],
            title: 'Performance Dashboard',
            description: 'Access the Performance Dashboard your entry point to the performance management module',
            permissions: [
                { type: 'dashboard', label: 'Dashboard Access' }
            ],
            requirements: 'Dashboard Access feature role',
            additionalInfo: `
                <p><strong> Key Features:</strong></p>
                <ul>
                    <li>Perforamce setup overview </li>
                    <li>Learn how to navigate and use the performance module</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                '[data-tutorial-element="plans-table"]'
            ],
            title: 'Objective Plans Summary',
            description: 'View a summary of all performance objective plans for the selected employee.',
            permissions: [
                { type: 'individual', label: 'Individual Access' }
            ],
            requirements: 'All users can view their own objective plans.',
            additionalInfo: `
                <p><strong>Columns:</strong></p>
                <ul>
                    <li><strong>Plan Name: Objective plan title</li>
                    <li><strong>Cycle:</strong> Performance period</li>
                    <li><strong>Due Date:</strong> Completion deadline</li>
                    <li><strong>Current Stage:</strong> Progress status</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },
        {
            element: [
                '[data-tutorial-element="action-buttons"]'
            ],
            title: 'Plan Action Buttons',
            description: 'Perform key actions for each objective plan.',
            permissions: [
                { type: 'individual', label: 'View Access' },
                { type: 'objectives', label: 'Edit Access' },
                { type: 'admin', label: 'Delete Access' }
            ],
            additionalInfo: `
                <p><strong>Button Functions:</strong></p>
                <ul>
                    <li><strong>View:</strong> See full plan details (available to all users)</li>
                    <li><strong>Edit:</strong> Update plan settings (requires Objectives Management role)</li>
                    <li><strong>Delete:</strong> Permanently remove a plan (Admin only)</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        {
            element: [
                '[data-tutorial-element="progress-timeline"]'
            ],
            title: 'Progress Timeline',
            description: 'See a visual overview of each objective plan’s progress and stages.',
            permissions: [
                { type: 'individual', label: 'View Access' },
                { type: 'objectives', label: 'Stage Management' }
            ],
            requirements: 'Individual, Objective, or Admin roles',
            additionalInfo: `
                <p><strong>Status Indicators:</strong></p>
                <ul>
                    <li>🟢<strong>Completed:</strong> Stage finished</li>
                    <li>🔵<strong>Active:</strong> Current stage</li>
                    <li>⚪<strong>Not Started:</strong> Future stages</li>
                </ul>
                <p><strong>Additional Info:
                <p><small>Users with Objectives or Admin access can advance stages using the “Mark as Done” button.</small></p>
                <p><strong>Select an objective plan to view its progress timeline.</strong></p>
            `,
            position: 'left',
            optional: false
        },
        {
            element: [
                'button[data-bind*="start_review_process"]',
                '[data-tutorial-element="start-review-btn"]',
                '[data-bind="visible: canStartReview"]'
            ],
            title: 'Start Review Process',
            description: 'Begin the formal performance review and feedback process.',
            permissions: [
                { type: 'admin', label: 'Admin Access' }
            ],
            requirements: 'Performance Admin feature role' ,
            additionalInfo: `
                <p><strong>What it does:</strong></p>
                <ul>
                    <li>Enables feedback sections</li>
                    <li>Allows rating submissions</li>
                    <li>Activates peer review process</li>
                    <li>Prepares for final evaluation</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                'button[data-bind*="btn_add_peers"]',
                '[data-tutorial-element="add-peers-btn"]',
                '.ps-btn-primary[title*="Add peer members"]'
            ],
            title: 'Add Peer Reviewers',
            description: 'Select colleagues to provide peer feedback on performance objectives.',
            permissions: [
                { type: 'admin', label: 'Admin Access' },
                { type: 'individual', label: 'Individual Access' }
            ],
            requirements: 'Performance Admin feature role (for cross-department selection); all users (for same-department selection)',
            additionalInfo: `
                <p><strong>Purpose:</strong></p>
                <ul>
                    <li>Enable 360° feedback collection</li>
                    <li>Support multi-perspective evaluations</li>
                    <li>Create a comprehensive performance assessment</li>
                </ul>
                <p><strong>Additional Info:
                <p><small>Users with Performance Admin feature role can add employees from different departments as peers for employee</small></p>
                <ul>
                    <li>Users with the Performance Admin role can add peersfrom any department.</li>
                    <li>All users can add peers from their own department.</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                '[data-bind*="show_feedback_section"]',
                '[data-tutorial-element="rating-table"]',
                '[data-bind="visible: canViewEmployeeRating"]'
            ],
            title: 'Feedback & Rating Section',
            description: 'Collect and manage feedback and performance ratings.',
            permissions: [
                { type: 'employee', label: 'Self-Rating' },
                { type: 'manager', label: 'Manager Rating' },
                { type: 'individual', label: 'View Peer Ratings' }
            ],
            requirements: 'Employee, Manager, and Individual roles',
            additionalInfo: `
                <p><strong>Rating Types:</strong></p>
                <ul>
                    <li><strong>Employee Self-Rating: Employees assess their own performance</li>
                    <li><strong>Manager Rating: Managers rate their direct reports</li>
                    <li><strong>Peer Ratings:</strong>Colleague feedback (view-only for most users)</li>
                </ul>
                <p>Visible once the review process is started by an admin.</p>
            `,
            position: 'left',
            optional: false,
            helperNumberStyle: 'opacity: 1; left: 36em; top: 5em;'
        },
        {
            element: [
                'button[data-bind*="publish_rating"]',
                '[data-tutorial-element="publish-rating-btn"]',
                '.ps-btn-primary[title*="Publish ratings"]'
            ],
            title: 'Publish Final Ratings',
            description: 'Publish final performance ratings and feedback for employees.',
            permissions: [
                { type: 'admin', label: 'Admin Access' }
            ],
            requirements: 'Performance Admin role',
            additionalInfo: `
                <p><strong>What happens:</strong></p>
                <ul>
                    <li>Final ratings become visible to employees</li>
                    <li>Notifications may be sent</li>
                    <li>Performance cycle is marked complete</li>
                    <li>Further rating changes are locked</li>
                </ul>
                <p style="color: #ff9800;">⚠️ <strong>Warning:</strong> This action is typically irreversible.</p>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                'button[data-bind*="btn_add_objective_to_plan"]',
                '[data-tutorial-element="add-objective-btn"]',
                '.ps-btn-primary[title*="Add objective"]'
            ],
            title: 'Add New Objective',
            description: 'Add individual objectives to the selected performance plan.',
            permissions: [
                { type: 'objectives', label: 'Objectives Access' },
                { type: 'admin', label: 'Admin Access' }
            ],
            requirements: 'Objectives Management or Performance Admin roles',
            additionalInfo: `
                <p><strong>Objective Components:</strong></p>
                <ul>
                    <li>Objective name and description</li>
                    <li>Expected resultss</li>
                    <li>Key Performance Indicators (KPIs)</li>
                    <li>Status Tracking</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                '[data-tutorial-element="objectives-table"]'
            ],
            title: '📋 Objectives Management',
            description: 'View and manage individual objectives within the selected performance plan.',
            permissions: [
                { type: 'individual', label: 'View Access' },
                { type: 'objectives', label: 'Edit Access' },
                { type: 'admin', label: 'Full Management' }
            ],
            additionalInfo: `
                <p><strong>Objective Information:</strong></p>
                <ul>
                    <li><strong>Sequence:</strong> Objective order and priority (Objectives users can reorder</li>
                    <li><strong>Details:</strong> Objective name, description, and expected outcomes</li>
                    <li><strong>Measures:</strong> Key Performance Indicators (KPIs) and success criteria</li>
                    <li><strong>Status:</strong> Progress tracking (all users can update)</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                '[data-tutorial-element="objective-status"]'

            ],
            title: 'Objective Status Management',
            description: 'Easily track and update objective progress',
            permissions: [
                { type: 'individual', label: 'Status Updates' }
            ],
            requirements: 'All users',
            additionalInfo: `
                <p><strong>Status Options:</strong></p>
                <ul>
                    <li><strong>New:</strong> Just created</li>
                    <li> <strong>In Progress:</strong> Actively being worked on</li>
                    <li><strong>On Hold:</strong> Temporarily paused</li>
                    <li> <strong>Obsolete:</strong> No longer relevant</li>
                    <li> <strong>Finished:</strong> Successfully completed</li>
                </ul>
                <p><strong>Quick Actions:</strong></p>
                <ul>
                    <li><strong>Green Button:</strong>Move to next status</li>
                    <li><strong>Yellow Button:</strong> Revert to previous status</li>
                    <li><strong>Edit Button:</strong> Manually select any status</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                'input[data-bind*="showing_feedback"]',
                '[data-tutorial-element="feedback-toggle"]',
                '.ps-switch input[data-bind*="showing_feedback"]'
            ],
            title: 'Detailed Feedback Toggle',
            description: 'Show or hide detailed feedback sections for each objective.',
            permissions: [
                { type: 'individual', label: 'View Access' },
                { type: 'manager', label: 'Manager Feedback' },
                { type: 'employee', label: 'Employee Feedback' }
            ],
            requirements: 'All users (Employee, Manager, Individual roles)',
            additionalInfo: `
                <p><strong>Displayed When Enabled:</strong></p>
                <ul>
                    <li><strong>Linked Company Objectives:</strong> Connects individual goals to company objectives</li>
                    <li><strong>Manager Feedback:</strong> Supervisor comments (editable by managers)</li>
                    <li><strong>Employee Feedback:</strong> Self-assessment comments (editable by employees)</li>
                </ul>
            `,
            position: 'right',
            optional: true
        },
        {
            element: [
                'input[data-bind*="show_peers"]',
                '.ps-switch input[data-bind*="show_peers"]'
            ],
            title: 'Show Peer Names Toggle',
            description: 'Control whether peer reviewer names are visible in feedback sections.',
            permissions: [
                { type: 'admin', label: 'Admin Access' }
            ],
            requirements: 'Performance admin feature role',
            additionalInfo: `
                <p><strong>Purpose:</strong></p>
                <ul>
                    <li><strong>Anonymous Reviews:</strong> Peer names hidden, labeled as “Peer Rating”</li>
                    <li><strong>Named Reviews:</strong> Actual reviewer names shown</li>
                    <li><strong>Privacy Control:</strong> Adjust visibility for transparency or anonymity</li>
                </ul>
                <p><strong>Best Practices:</strong></p>
                <ul>
                    <li>Keep anonymous for honest feedback</li>
                    <liActual reviewer names shown</li>
                    <li>Follow your company’s policy</li>
                </ul>
            `,
            position: 'right',
            optional: true
        },
        {
            element: [ '[data-tutorial-element="perfm_manage_help"]'],
            title: 'Contextual Help',
            description: 'Need help? Click the info icon on any section for guidance',
            permissions: [{ type: 'admin', label: 'All Users' }],
            requirements: 'All users',
            additionalInfo: `
                <p><strong>Help includes:</strong></p>
                <ul>
                    <li>Details for each section</li>
                    <li>Explanations of complex features</li>
                    <li>Step-by-step guides</li>
                </ul>
                <p><small>Always check help if you're unsure!</small></p>
            `,
            position: 'left',
            optional: false
        }
    ],
    
    mockDataCreators: {

        objectives: function() {
            if (this.objectives && this.objectives().length === 0) {
                const mockObjective = {
                    objective_id: 'tutorial_mock',
                    name: 'Sample Objective (Tutorial)',
                    description: 'This is a sample objective shown for tutorial purposes',
                    expected_result: 'Complete tutorial understanding',
                    measure: 'User comprehension',
                    status: ko.observable('In Progress'),
                    sequence: 1,
                    editing: ko.observable(false),
                    editing_emp_feedback: ko.observable(false),
                    editing_manager_feedback: ko.observable(false),
                    selected_status: ko.observable('In Progress'),
                    status_update_visible: ko.observable(false),
                    toggleStatusUpdate: function() {
                        this.status_update_visible(!this.status_update_visible());
                    },
                    linked_company_objectives: ko.observable([]),
                    manager_feedback: 'Sample manager feedback for tutorial',
                    employee_feedback: 'Sample employee feedback for tutorial'
                };

                const currentObjectives = this.objectives();
                currentObjectives.unshift(mockObjective);
                this.objectives(currentObjectives);
            }
        },

        peers: function() {
            console.log('🎭 Creating mock peers data');
            if (this.peer_members && this.peer_members().length === 0) {
                const mockPeer = {
                    peer_name: 'John Doe (Tutorial Sample)',
                    rating: '4',
                    notes: 'Sample peer feedback for tutorial purposes',
                    peer_member_id: 'tutorial_mock_peer',
                    peer_rating_observable: ko.observable(4),
                    peer_ratings_observable: ko.observable([4])
                };

                const currentPeers = this.peer_members();
                currentPeers.push(mockPeer);
                this.peer_members(currentPeers);
            }
        }
    },

    mockDataCleaners: {
        objectives: function() {
            if (this.objectives) {
                const objectives = this.objectives();
                const filtered = objectives.filter(obj => obj.objective_id !== 'tutorial_mock');
                this.objectives(filtered);
            }
        },

        peers: function() {
            if (this.peer_members) {
                const peers = this.peer_members();
                const filtered = peers.filter(peer => peer.peer_member_id !== 'tutorial_mock_peer');
                this.peer_members(filtered);
            }
        },

        clearObjectivePlans: function() {
            this.selected_plan(null);
        },
        clearRatings: function() {
            this.employee_ratings([]);
            this.manager_ratings([]);
            this.employee_rating('');
            this.manager_rating('');
            this.employee_note('');
            this.manager_note('');
        },
        // Add this new cleaner to reset the feedback section visibility
        resetFeedbackSection: function() {
            this.show_feedback_section(false);
            this.hide_start_review(false);
            this.show_publish_rating(false);
            this.is_published(false);
        }
    },
    
    additionalSelectors: [
        '[data-bind*="canEditPlan"]',
        '[data-bind*="canDeletePlan"]',
        '[data-bind*="canStartReview"]',  // This is already there
        '[data-tutorial-element="start-review-btn"]',  // Add direct element selector
        '[data-bind*="canPublishRating"]',
        '[data-bind*="canAddObjectives"]',
        '[style*="display: none"]',
        '[style*="visibility: hidden"]',
        '[data-bind*="visible:"]',
        '[data-visible-feature]'
    ],
};